import moment from "moment-timezone";
import _ from "lodash";
import * as dateFnLocales from "date-fns/locale";

export const DEFAULT_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const MS_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss.SSS";
export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

const SECONDS_TO_MILLISECONDS = 1000;

export function isDateTime(val) {
  return moment.isMoment(val);
}

export function isDateType(val) {
  return moment(val).isValid();
}

export function currentDateTime() {
  return moment().format(DEFAULT_DATE_TIME_FORMAT);
}

export function getSubtractedDateFromNow(timeAgo, formatInput = undefined) {
  return moment()
    .subtract(timeAgo, "days")
    .format(formatInput || DEFAULT_DATE_FORMAT);
}

export function toUtc(input, formatInput) {
  return moment
    .tz(input, getTimeZone())
    .utc()
    .format(formatInput || DEFAULT_DATE_TIME_FORMAT);
}

function toTz(dateInUtc, timeZone) {
  const date = dateInUtc;
  const zone = moment.tz.zone(timeZone);

  if (zone && zone.name) {
    return moment.utc(date).tz(zone.name);
  }

  switch (timeZone) {
    case "utc":
      return moment.utc(date);
    default:
      return moment.utc(date).local();
  }
}

export function getTimeZone(options) {
  return (
    options?.timeZone ??
    Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ??
    "browser"
  );
}

export function currentDateTimeUtc() {
  return moment.utc().format(DEFAULT_DATE_TIME_FORMAT);
}

function getFormat(options) {
  if (options?.defaultWithMS) {
    return options?.format ?? MS_DATE_TIME_FORMAT;
  }
  return options?.format ?? DEFAULT_DATE_TIME_FORMAT;
}

function getDateFormat(options) {
  return options?.format ?? DEFAULT_DATE_FORMAT;
}

export function dateTimeFormat(dateInUtc, options = undefined) {
  return toTz(dateInUtc, getTimeZone(options)).format(getFormat(options));
}

export function dateFormat(dateInUtc, options = undefined) {
  return toTz(dateInUtc, getTimeZone(options)).format(getDateFormat(options));
}

export function dateTimeFormatISO(dateInUtc, options) {
  return toTz(dateInUtc, getTimeZone(options)).format();
}

export function dateTimeFormatTimeAgo(dateInUtc, options = undefined) {
  return toTz(dateInUtc, getTimeZone(options)).fromNow();
}

export const GLOBAL_CURRENT_DATETIME = new Date();

export function resolveLocaleDate() {
  const localeName = new Intl.DateTimeFormat().resolvedOptions().locale;
  const normalizedLocaleName = localeName.replace("-", "");
  if (_.has(dateFnLocales, normalizedLocaleName)) {
    return dateFnLocales[normalizedLocaleName];
  } else if (_.has(dateFnLocales, localeName.split("-")[0])) {
    return dateFnLocales[localeName.split("-")[0]];
  } else {
    return null;
  }
}

export function unixEpochToDateTime(unixDateTime, resolution, offset) {
  return unixDateTime + resolution * SECONDS_TO_MILLISECONDS * offset;
}

export function getNumberOfDays(start, end) {
  const date1 = new Date(start);
  const date2 = new Date(end);

  // One day in milliseconds
  const oneDay = 1000 * 60 * 60 * 24;

  // Calculating the time difference between two dates
  const diffInTime = date2.getTime() - date1.getTime();

  // Calculating the no. of days between two dates
  return Math.round(diffInTime / oneDay);
}

/**
 * Checks if the user's locale is currently supported.
 * If it is not supported, we should use the ISO date format as the default.
 *
 * This function is currently necessary due to limitations of the current date pickers and will be removed in the future.
 * @param localeCode the code of locale to check (E.g. en-GB)
 * @returns {boolean} Whether the locale is currently supported
 */
export const isSupportedLocale = localeCode =>
  [
    "en-GB",
    "en-US",
    "de-DE",
    "de",
    "hu-HU",
    "hu",
    "pt-PT",
    "pt-BR",
    "es-ES",
    "es",
    "ja-JP",
    "ja",
    "pt",
    "zh-CN",
    "vi"
  ].includes(localeCode);
